import { Box, InputAdornment, Select, SelectOption } from '@chronosphereio/chrono-ui';
import ImageFilterCenterFocus from 'mdi-material-ui/ImageFilterCenterFocus';
import { useMemo } from 'react';
import { ClusterLayout } from './build-cluster-layout';
import { DependencyMapNodeType } from './dependency-map-config';

export interface Focus {
  /** Index into the `clusters` array. */
  clusterIdx: number;
  /** Real node id (aggregates aren't focusable — their click is a no-op). */
  nodeId: string;
}

interface NodeFocusSelectProps {
  clusters: ClusterLayout[];
  focus: Focus | null;
  nodeType: DependencyMapNodeType;
  /**
   * Invoked when the user picks a node, clears the dropdown, or re-picks
   * the synthetic all-option. The parent is expected to coordinate the
   * pager (jump to `next.clusterIdx`) and any related state.
   */
  onFocusChange: (next: Focus | null) => void;
}

/**
 * Dropdown for picking a node to focus on. Owns the synthetic "all" option,
 * the cluster-id reverse-index, and aggregate-label fallback resolution;
 * emits a semantic `Focus | null` so the parent doesn't need to know the
 * dropdown's internals.
 */
export function NodeFocusSelect({ clusters, focus, nodeType, onFocusChange }: NodeFocusSelectProps) {
  // Synthetic "no focus" option. Memoized for stable `value` ref across renders.
  const allOption = useMemo<SelectOption>(() => ({ label: `All ${nodeType.plural}`, key: '' }), [nodeType.plural]);

  // Real node id → cluster idx so dropdown picks can jump the pager. Aggregate
  // ids are omitted (not unique across clusters); resolved via `Focus` instead.
  const clusterIdxByNode = useMemo(() => {
    const map = new Map<string, number>();
    clusters.forEach((cluster, idx) => {
      cluster.rawLinks.forEach(({ from, to }) => {
        map.set(from, idx);
        map.set(to, idx);
      });
    });
    return map;
  }, [clusters]);

  const nodeOptions = useMemo<SelectOption[]>(
    () => [allOption, ...[...clusterIdxByNode.keys()].sort().map((id) => ({ label: id, key: id }))],
    [allOption, clusterIdxByNode]
  );

  // `Focus.nodeId` is read-only (aggregates aren't focusable);
  // the cluster-node fallback is defensive against future drift.
  const focusOption = useMemo<SelectOption | null>(() => {
    if (focus === null) return null;

    const existing = nodeOptions.find((o) => o.key === focus.nodeId);
    if (existing) return existing;

    const node = clusters[focus.clusterIdx]?.nodes.find((n) => n.id === focus.nodeId);

    return { label: node?.label ?? focus.nodeId, key: focus.nodeId };
  }, [focus, nodeOptions, clusters]);

  const dropdownOptions = useMemo<SelectOption[]>(() => {
    if (focusOption === null || nodeOptions.some((o) => o.key === focusOption.key)) return nodeOptions;
    return [...nodeOptions, focusOption];
  }, [nodeOptions, focusOption]);

  return (
    <Box sx={{ width: 280 }}>
      {/*
       * `Select` is deprecated, but `SimpleSelect` lacks autocomplete and
       * `ComboboxSelect` hides its clear button behind a popover — neither
       * fits. `Select` is virtualized via `VirtualizedAutocomplete`.
       */}
      <Select
        Label={`Focus on ${nodeType.singular}`}
        // Hide clear when nothing's focused (value is the synthetic all-option).
        disableClearable={focus === null}
        // Always show the clear X when focused — primary affordance after drill-in.
        sx={{ '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' } }}
        // Adornment so the focused state reads at a glance.
        InputProps={
          focus === null
            ? undefined
            : {
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageFilterCenterFocus fontSize="small" />
                  </InputAdornment>
                ),
              }
        }
        options={dropdownOptions}
        value={focusOption ?? allOption}
        onChange={(_, newValue) => {
          // Clear button or all-option both reset focus.
          if (!newValue?.key) {
            onFocusChange(null);
            return;
          }

          const id = String(newValue.key);
          const clusterIdx = clusterIdxByNode.get(id);

          // Defensive: every real id has a `clusterIdxByNode` entry (it's
          // the source for `nodeOptions`). Skip if missing rather than guess.
          if (clusterIdx === undefined) return;

          onFocusChange({ clusterIdx, nodeId: id });
        }}
      />
    </Box>
  );
}
